import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';

interface ConfirmationModalProps {
  onClose: () => void;
  onConfirm: (code: number) => void;
  verificationCode: string;
  setVerificationCode: (code: string) => void;
  isLoading: boolean;
  phoneNumber?: string;
  /** Seconds before a new code may be requested; the api returns this as `ttl`. */
  ttl?: number;
  onResend?: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  onClose,
  onConfirm,
  verificationCode,
  setVerificationCode,
  isLoading,
  phoneNumber,
  ttl = 120,
  onResend,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(ttl);

  useEffect(() => {
    setSecondsLeft(ttl);
  }, [ttl]);

  useEffect(() => {
    if (secondsLeft <= 0) return;

    const id = setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(id);
  }, [secondsLeft]);

  const formattedTime = `${String(Math.floor(secondsLeft / 60)).padStart(
    2,
    '0',
  )}:${String(secondsLeft % 60).padStart(2, '0')}`;

  const handleResend = () => {
    if (!onResend) return;
    onResend();
    setSecondsLeft(ttl);
  };

  const handleVerificationCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setVerificationCode(event.target.value);
  };

  const handleConfirm = () => {
    const codeAsNumber = parseInt(verificationCode, 10);

    // اعتبارسنجی کد 6 رقمی
    if (isNaN(codeAsNumber) || codeAsNumber < 100000 || codeAsNumber > 999999) {
      alert('کد تأیید باید یک عدد معتبر 6 رقمی باشد.');
      return;
    }

    onConfirm(codeAsNumber);
  };

  return (
    <Box
      sx={{
        width: '420px',
        minHeight: '320px',
        padding: '24px',
        background: 'white',
        borderRadius: 2,
        border: '1px #E5E5E8 solid',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'right',
        gap: 3,
        display: 'inline-flex',
      }}
    >
      <Typography
        fontFamily="IRANYekanXFaNum"
        fontSize="16px"
        textAlign="right"
        fontWeight="700"
        paddingBlock="10px"
        color="#25282E"
        sx={{ alignSelf: 'stretch', textAlign: 'right' }}
      >
        تأیید تغییر هویت
      </Typography>
      <Box
        sx={{
          alignSelf: 'stretch',
          height: 50,
          paddingTop: 1,
          paddingBottom: 0,
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-end',
          gap: 4,
          display: 'flex',
        }}
      >
        <Typography
          fontFamily="IRANYekanXFaNum"
          fontSize="20px"
          fontWeight="900"
          color="#25282E"
          sx={{ width: 262, textAlign: 'right' }}
        >
          کد تایید را وارد نمایید
        </Typography>
        <Typography
          variant="body2"
          color="#60646E"
          sx={{
            alignSelf: 'stretch',
            textAlign: 'right',
            fontFamily: 'IRANYekanXFaNum',
            fontSize: '14px',
            gap: 4,
          }}
        >
          {phoneNumber
            ? `کد تایید ارسال شده به شماره ${phoneNumber} را وارد نمایید`
            : 'کد تایید ارسال شده را وارد نمایید'}
        </Typography>
      </Box>
      <Box
        sx={{
          alignSelf: 'stretch',
          height: 126,
          width: 310,
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-end',
          display: 'flex',
        }}
      >
        <Typography
          color="#35373C"
          sx={{
            textAlign: 'right',
            fontFamily: 'IRANYekanXFaNum',
            fontSize: '14px',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          کد تایید
        </Typography>
        <Box
          sx={{
            width: 300.33,
            height: 40,
            position: 'relative',
            borderRadius: 1,
            border: '1px #D9D9DD solid',
          }}
        >
          <TextField
            variant="outlined"
            value={verificationCode}
            onChange={handleVerificationCodeChange}
            inputProps={{
              maxLength: 6,
              style: {
                textAlign: 'center',
                fontFamily: 'IRANYekanXFaNum',
                fontSize: '16px',
                letterSpacing: '10px',
              },
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            placeholder="- - - - - -"
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                border: '1px #D9D9DD solid',
                height: 40,
              },
              '& .MuiInputBase-input::placeholder': {
                opacity: 1,
                color: '#BBC0CB',
                letterSpacing: '10px',
                fontSize: '16px',
              },
            }}
          />
        </Box>
        {secondsLeft > 0 ? (
          <Typography
            variant="caption"
            color="#A6AAB5"
            sx={{
              alignSelf: 'stretch',
              paddingLeft: 16,
              paddingRight: 16,
              borderRadius: 1,
              textAlign: 'center',
              fontFamily: 'IRANYekanXFaNum',
            }}
          >
            {formattedTime} تا دریافت مجدد
          </Typography>
        ) : (
          <Button
            variant="text"
            onClick={handleResend}
            disabled={isLoading || !onResend}
            sx={{
              alignSelf: 'stretch',
              color: '#2563EB',
              fontFamily: 'IRANYekanXFaNum',
              fontSize: '12px',
              fontWeight: 700,
            }}
          >
            ارسال مجدد کد
          </Button>
        )}
      </Box>
      <Box
        sx={{
          alignSelf: 'stretch',
          justifyContent: 'space-between',
          alignItems: 'center',
          display: 'inline-flex',
          padding: 1,
        }}
      >
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
          sx={{
            backgroundColor: '#2563EB',
            color: 'white',
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 10,
            paddingBottom: 10,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: '#1E40AF',
            },
            '&:disabled': {
              backgroundColor: '#9CA3AF',
            },
          }}
        >
          <Typography variant="body2" fontWeight="700">
            {isLoading ? 'در حال ارسال...' : 'تایید و ارسال اطلاعات'}
          </Typography>
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isLoading}
          sx={{
            borderColor: '#9CA3AF',
            color: '#6B7280',
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 10,
            paddingBottom: 10,
            borderRadius: 1,
            gap: 4,
            '&:hover': {
              borderColor: '#6B7280',
              backgroundColor: '#F3F4F6',
            },
          }}
        >
          <Typography variant="body2" fontWeight="500">
            انصراف
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

export default ConfirmationModal;
